'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/common/Header';
import FilterSidebar from './FilterSidebar';
import SearchBar from './SearchBar';
import ProductCard from './ProductCard';
import Icon from '@/components/ui/AppIcon';
import { getProductsPage, getCategories, ApiCategory } from '@/lib/api';

const PAGE_SIZE = 24;

interface PricingTier {
  minQuantity: number;
  pricePerUnit: number;
  discount?: number;
}

interface PurchaseOption {
  type: 'pack' | 'box';
  label: string;
  unitsPerOption: number;
  basePrice: number;
  pricingTiers?: PricingTier[];
}

interface Product {
  id: string;
  name: string;
  category: string;
  categoryName: string;
  image: string;
  alt: string;
  purchaseOptions: PurchaseOption[];
  minPackQuantity: number;
  minBoxQuantity: number;
  inStock: boolean;
  popularity: number;
}

interface FilterOptions {
  categories: string[];
  priceRange: {
    min: number;
    max: number;
  };
  sortBy: 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc' | 'popularity';
  inStockOnly: boolean;
}

interface CartItem {
  productId: string;
  quantity: number;
  optionType: 'pack' | 'box';
}

const ProductCatalogInteractive = () => {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    categories: [],
    priceRange: { min: 0, max: 100000 },
    sortBy: 'popularity',
    inStockOnly: false,
  });
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreLock = useRef(false);
  const pageRef = useRef(1);

  useEffect(() => {
    setIsHydrated(true);
    const savedCart = localStorage.getItem('wholesaleCart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch {
        setCartItems([]);
      }
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    getCategories()
      .then((categoriesRes) => {
        if (cancelled) return;
        setCategories(categoriesRes);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Не удалось загрузить категории');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryFromUrl = searchParams.get('category')?.trim() ?? '';

  useEffect(() => {
    if (!categoryFromUrl) return;
    if (categories.length === 0) return;
    if (!categories.some((c) => c.slug === categoryFromUrl)) return;
    setFilters((prev) => {
      if (prev.categories.length === 1 && prev.categories[0] === categoryFromUrl) {
        return prev;
      }
      return { ...prev, categories: [categoryFromUrl] };
    });
  }, [categoryFromUrl, categories]);

  const fetchParams = useCallback(
    (page: number, signal?: AbortSignal) => {
      const q = debouncedSearch.trim();
      return {
        page,
        page_size: PAGE_SIZE,
        signal,
        search: q || undefined,
        categories: filters.categories.length > 0 ? filters.categories : undefined,
        sort_by: filters.sortBy,
        price_min: filters.priceRange.min,
        price_max: filters.priceRange.max,
        in_stock: filters.inStockOnly ? true : undefined,
      };
    },
    [
      debouncedSearch,
      filters.categories,
      filters.sortBy,
      filters.priceRange.min,
      filters.priceRange.max,
      filters.inStockOnly,
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      loadMoreLock.current = false;
      pageRef.current = 1;
      try {
        const res = await getProductsPage(fetchParams(1, abortController.signal));
        if (cancelled || abortController.signal.aborted) return;
        setProducts(res.results as Product[]);
        setTotalCount(res.count);
        setHasMore(res.has_more);
      } catch (err) {
        if (cancelled || abortController.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Не удалось загрузить каталог');
        setProducts([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        if (!cancelled && !abortController.signal.aborted) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [fetchParams]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || loadingMore || loadMoreLock.current) return;
    loadMoreLock.current = true;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const res = await getProductsPage(fetchParams(next));
      pageRef.current = next;
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const nextList = [...prev];
        for (const p of res.results) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            nextList.push(p as Product);
          }
        }
        return nextList;
      });
      setHasMore(res.has_more);
    } catch {
      // keep hasMore; user can scroll again
    } finally {
      setLoadingMore(false);
      loadMoreLock.current = false;
    }
  }, [hasMore, loading, loadingMore, fetchParams]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) loadMore();
      },
      { rootMargin: '240px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadMore]);

  const availableCategories = categories.map((c) => ({
    id: c.slug,
    name: c.name,
    count: c.productCount ?? 0,
  }));

  const handleAddToCart = (productId: string, quantity: number, optionType: 'pack' | 'box') => {
    if (!isHydrated) return;

    const existingItemIndex = cartItems.findIndex(
      (item) => item.productId === productId && item.optionType === optionType
    );

    let updatedCart: CartItem[];
    if (existingItemIndex >= 0) {
      updatedCart = [...cartItems];
      updatedCart[existingItemIndex].quantity += quantity;
    } else {
      updatedCart = [...cartItems, { productId, quantity, optionType }];
    }

    setCartItems(updatedCart);
    localStorage.setItem('wholesaleCart', JSON.stringify(updatedCart));

    const product = products.find((p) => p.id === productId);
    setToastMessage(`${product?.name ?? 'Товар'} добавлен в заявку (${quantity} шт)`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <Header cartCount={0} />
        <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
          <div className="mb-8 flex items-center justify-between">
            <div className="h-10 w-48 animate-pulse rounded-md bg-muted" />
            <div className="h-10 w-32 animate-pulse rounded-md bg-muted" />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-lg bg-card" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header cartCount={cartCount} />
        <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
          <div className="flex flex-col items-center justify-center rounded-lg bg-card p-12 text-center">
            <Icon name="ExclamationCircleIcon" size={48} className="mb-4 text-error" />
            <h2 className="mb-2 font-heading text-xl font-semibold text-text-primary">
              Ошибка загрузки каталога
            </h2>
            <p className="font-body text-base text-text-secondary">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header cartCount={cartCount} />

      <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
        <div className="mb-8">
          <h1 className="mb-2 font-heading text-3xl font-bold text-text-primary lg:text-4xl">
            Каталог товаров
          </h1>
          <p className="font-body text-base text-text-secondary">
            Найдено товаров: {totalCount}
          </p>
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchBar onSearch={setSearchQuery} initialValue={searchQuery} />
          </div>
          <button
            onClick={() => setIsMobileFilterOpen(true)}
            className="flex items-center justify-center gap-2 rounded-lg bg-card px-4 py-3 font-heading text-sm font-medium text-text-primary shadow-sm transition-all duration-250 hover:shadow-md active:scale-95 lg:hidden"
          >
            <Icon name="AdjustmentsHorizontalIcon" size={20} />
            <span>Фильтры</span>
            {(filters.categories.length > 0 || filters.inStockOnly) && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-xs font-medium text-accent-foreground">
                {filters.categories.length + (filters.inStockOnly ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        <div className="flex items-start gap-6">
          <FilterSidebar
            filters={filters}
            onFilterChange={setFilters}
            availableCategories={availableCategories}
            isMobileOpen={isMobileFilterOpen}
            onMobileClose={() => setIsMobileFilterOpen(false)}
          />

          <div className="flex-1">
            {loading && products.length === 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-96 animate-pulse rounded-lg bg-card" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg bg-card p-12 text-center">
                <Icon name="MagnifyingGlassIcon" size={48} className="mb-4 text-text-secondary" />
                <h3 className="mb-2 font-heading text-xl font-semibold text-text-primary">
                  Товары не найдены
                </h3>
                <p className="font-body text-base text-text-secondary">
                  Попробуйте изменить параметры поиска или фильтры
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                  ))}
                </div>
                {hasMore ? (
                  <div ref={sentinelRef} className="flex justify-center py-8" aria-hidden>
                    {loadingMore ? (
                      <span className="font-caption text-sm text-text-secondary">Загрузка…</span>
                    ) : (
                      <span className="h-8 w-8" />
                    )}
                  </div>
                ) : null}
                {loading && !loadingMore ? (
                  <div className="py-4 text-center">
                    <span className="font-caption text-sm text-text-secondary">Обновляем список…</span>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </main>

      {showToast && (
        <div className="fixed bottom-6 right-6 z-[300] animate-slide-up">
          <div className="flex items-center gap-3 rounded-lg bg-success px-6 py-4 shadow-xl">
            <Icon name="CheckCircleIcon" size={24} className="text-success-foreground" />
            <p className="font-heading text-sm font-medium text-success-foreground">
              {toastMessage}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalogInteractive;
